import { useQuery } from '@tanstack/react-query'
import { api } from '../../../lib/api'

/* ───── Types ───── */

export interface StatItem {
  /** Unique key identifier for this stat (e.g. "total_entities") */
  key: string
  /** Human-readable label */
  label: string
  /** Current numeric value */
  value: number
  /** Absolute delta vs previous period */
  delta: number
  /** Percentage change vs previous period (e.g. 5.2 means +5.2%) */
  deltaPercent: number
  /** Description of the delta period (e.g. "vs last week") */
  deltaPeriod: string
  /** Icon identifier for the stat card */
  icon: 'messages' | 'entities' | 'groups' | 'messages24h'
}

interface DashboardStatsResponse {
  stats: StatItem[]
}

/* ───── Query Key ───── */

export const dashboardStatsQueryKey = ['dashboard', 'stats'] as const

/* ───── Hook ───── */

/**
 * TanStack Query hook that fetches dashboard stat counters from
 * GET /api/v1/dashboard/stats.
 *
 * Returns an array of StatItem objects for the 4 main counters:
 * Total Messages, Total Entities, Total Groups, Messages/24h.
 *
 * Uses a 5-minute staleTime to avoid over-fetching on frequent
 * dashboard visits.
 */
export function useDashboardStats(autoRefresh = true) {
  const query = useQuery({
    queryKey: dashboardStatsQueryKey,
    queryFn: ({ signal }) =>
      api<DashboardStatsResponse>('/v1/dashboard/stats', { signal }),
    staleTime: 30_000,         // 30 seconds (matches refetch interval)
    gcTime: 10 * 60 * 1000,   // 10 minutes garbage collection
    refetchInterval: autoRefresh ? 30_000 : false, // 30s polling
  })

  return {
    stats: query.data?.stats ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
  }
}
